/**
 * Error Context Provider
 * Global error handling with toast notifications
 */

import { useCallback, useEffect, useState } from 'react';

import { ErrorToast } from '@/components/ErrorToast';

import {
  ERROR_RECOVERY_SUGGESTIONS,
  ErrorContext,
  ErrorContextValue,
  ErrorDetails,
  ErrorProviderProps,
  ErrorSeverity,
} from './errorContextTypes';

const DEFAULT_MAX_ERRORS = 5;
const DEFAULT_AUTO_HIDE_DURATION = 5000; // 5 seconds for info/warning, no auto-hide for errors

export function ErrorProvider({
  children,
  maxErrors = DEFAULT_MAX_ERRORS,
  autoHideDuration = DEFAULT_AUTO_HIDE_DURATION,
}: ErrorProviderProps) {
  const [errors, setErrors] = useState<ErrorDetails[]>([]);

  // Add a new error
  const addError = useCallback(
    (error: Omit<ErrorDetails, 'id' | 'timestamp'>) => {
      const id = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date();

      // Add suggestion if we have a known error code
      const suggestion =
        error.suggestion ||
        (error.code &&
          ERROR_RECOVERY_SUGGESTIONS[error.code as keyof typeof ERROR_RECOVERY_SUGGESTIONS]);

      const newError: ErrorDetails = {
        ...error,
        id,
        timestamp,
        severity: error.severity || 'error',
        suggestion,
      };

      setErrors((prev) => {
        // Limit the number of errors
        const updated = [...prev, newError];
        if (updated.length > maxErrors) {
          return updated.slice(-maxErrors);
        }
        return updated;
      });

      // Log errors in development
      if (process.env.NODE_ENV === 'development') {
        const logMethod =
          error.severity === 'critical' || error.severity === 'error'
            ? console.error
            : error.severity === 'warning'
              ? console.warn
              : console.info;

        logMethod(`[${error.severity.toUpperCase()}]`, error.message, error.details || '');
      }
    },
    [maxErrors]
  );

  // Backward compatibility - simple showError method
  const showError = useCallback(
    (message: string) => {
      addError({ message, severity: 'error' });
    },
    [addError]
  );

  // Remove a specific error
  const removeError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((error) => error.id !== id));
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Get error count by severity
  const getErrorCount = useCallback(
    (severity?: ErrorSeverity) => {
      if (!severity) return errors.length;
      return errors.filter((error) => error.severity === severity).length;
    },
    [errors]
  );

  // Auto-hide info and warning messages
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    errors.forEach((error) => {
      if (error.severity === 'info' || error.severity === 'warning') {
        const timer = setTimeout(() => {
          removeError(error.id);
        }, autoHideDuration);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [errors, autoHideDuration, removeError]);

  const contextValue: ErrorContextValue = {
    errors,
    addError,
    removeError,
    clearErrors,
    getErrorCount,
    showError, // Backward compatibility
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {errors.map((error) => (
          <ErrorToast
            key={error.id}
            message={error.message}
            severity={error.severity}
            suggestion={error.suggestion}
            action={error.action}
            onClose={() => removeError(error.id)}
          />
        ))}
      </div>
    </ErrorContext.Provider>
  );
}
